import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, getDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { firestore, createUserByAdmin, deleteUserByAdmin } from '../../database/firebase';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiEye,
  HiTrash,
  HiXMark,
  HiUser,
  HiEnvelope,
  HiPhone,
  HiBuildingOffice2,
  HiCheckCircle,
  HiExclamationTriangle,
  HiInformationCircle,
  HiPlus,
  HiPencil,
  HiLockClosed,
  HiShieldCheck,
  HiUserGroup,
} from 'react-icons/hi2';
import toast from 'react-hot-toast';
import ConfirmationModal from '../../components/SharedFeature/ConfirmationModal';

const LoadingSpinner = () => (
  <motion.div
    className="flex flex-col items-center justify-center py-20"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <motion.div
      className="border-3 h-8 w-8 rounded-full border-blue-200 border-t-blue-600"
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
    />
    <p className="mt-4 text-sm text-gray-500">Loading alumni...</p>
  </motion.div>
);

const Toast = ({ message, type, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: -50, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -50, scale: 0.95 }}
    className={`fixed right-4 top-4 z-50 flex items-center gap-3 rounded-xl px-4 py-3 shadow-lg ${
      type === 'success'
        ? 'border border-green-200 bg-green-50'
        : type === 'error'
          ? 'border border-red-200 bg-red-50'
          : 'border border-gray-200 bg-gray-50'
    }`}
  >
    {type === 'success' && <HiCheckCircle className="h-5 w-5 text-green-600" />}
    {type === 'error' && <HiExclamationTriangle className="h-5 w-5 text-red-600" />}
    {type === 'info' && <HiInformationCircle className="h-5 w-5 text-blue-600" />}
    <span
      className={`text-sm font-medium ${type === 'success' ? 'text-green-800' : type === 'error' ? 'text-red-800' : 'text-black'}`}
    >
      {message}
    </span>
    <button
      onClick={onClose}
      className={`ml-2 hover:opacity-70 ${type === 'success' ? 'text-green-600' : type === 'error' ? 'text-red-600' : 'text-gray-600'}`}
    >
      <HiXMark className="h-4 w-4" />
    </button>
  </motion.div>
);

const AlumniTable = () => {
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    organization: '',
    businessStage: '',
    program: '',
  });
  const [creating, setCreating] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editAlumni, setEditAlumni] = useState(null);
  const [editForm, setEditForm] = useState({ firstname: '', lastname: '', email: '', phone: '' });
  const [updating, setUpdating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    alumni: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchAlumni();
  }, []);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchAlumni = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(firestore, 'alumni'));
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAlumni(data);
      if (data.length === 0) showToast('No alumni found', 'info');
    } catch (err) {
      setError('Failed to fetch alumni');
      showToast('Failed to load alumni', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async alumni => {
    setSelectedAlumni(alumni);
    setShowModal(true);
  };

  const handleDeleteClick = alumni => {
    setDeleteConfirm({
      isOpen: true,
      alumni,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.alumni) return;

    setIsDeleting(true);
    try {
      // Get the full alumni data
      const alumniDoc = await getDoc(doc(firestore, 'alumni', deleteConfirm.alumni.id));
      const alumniData = alumniDoc.data();

      // Add deletion metadata
      const deletedUserData = {
        ...alumniData,
        originalCollection: 'alumni',
        deletedAt: new Date().toISOString(),
        deletedBy: 'admin',
      };

      // Move to deleted_users collection
      await setDoc(doc(firestore, 'deleted_users', deleteConfirm.alumni.id), deletedUserData);

      // Delete from alumni collection
      await deleteDoc(doc(firestore, 'alumni', deleteConfirm.alumni.id));

      showToast('Alumni deleted and moved to deleted users!', 'success');
      setDeleteConfirm({ isOpen: false, alumni: null });
      fetchAlumni();
    } catch (err) {
      showToast(`Failed to delete alumni: ${err.message}`, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, alumni: null });
  };

  const generatePassword = () => {
    return Math.random().toString(36).slice(-8) + Math.random().toString(36).slice(-4);
  };

  const handleCreateAlumni = async e => {
    e.preventDefault();
    setCreating(true);
    const defaultPassword = 'Password@12345';
    try {
      const tempApp = initializeApp(
        {
          apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
          authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
          projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
          storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
          appId: process.env.REACT_APP_FIREBASE_APP_ID,
        },
        'temp-app-' + Date.now()
      );

      const tempAuth = getAuth(tempApp);

      const userCredential = await createUserWithEmailAndPassword(
        tempAuth,
        createForm.email,
        defaultPassword
      );

      const alumniData = {
        ...createForm,
        uid: userCredential.user.uid,
        id: userCredential.user.uid,
        role: 'alumni',
        status: 'active',
        createdAt: new Date(),
      };

      await setDoc(doc(firestore, 'alumni', userCredential.user.uid), alumniData);

      await tempAuth.signOut();

      setShowCreateModal(false);
      setGeneratedPassword(defaultPassword);
      setShowPasswordModal(true);
      setCreateForm({
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        organization: '',
        businessStage: '',
        program: '',
      });
      fetchAlumni();
      showToast('Alumni created successfully!', 'success');
    } catch (error) {
      let errorMessage = 'Failed to create alumni';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'A user with this email already exists';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = alumni => {
    setEditAlumni(alumni);
    setEditForm({
      firstname: alumni.firstname || '',
      lastname: alumni.lastname || '',
      email: alumni.email || '',
      phone: alumni.phone || '',
    });
  };

  const handleUpdateAlumni = async e => {
    e.preventDefault();
    setUpdating(true);
    try {
      await setDoc(doc(firestore, 'alumni', editAlumni.id), {
        ...editAlumni,
        ...editForm,
      });
      setEditAlumni(null);
      setEditForm({ firstname: '', lastname: '', email: '', phone: '' });
      fetchAlumni();
      showToast('Alumni updated successfully!', 'success');
    } catch (err) {
      showToast('Failed to update alumni', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleMakeClient = async alumni => {
    try {
      await setDoc(doc(firestore, 'clients', alumni.id), {
        ...alumni,
        status: 'Active',
        role: 'client',
      });
      await deleteDoc(doc(firestore, 'alumni', alumni.id));
      showToast('Alumni moved back to clients successfully!', 'success');
      fetchAlumni();
    } catch (err) {
      showToast('Failed to move alumni: ' + err.message, 'error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-black md:text-3xl">Alumni Management</h1>
              <p className="mt-2 text-sm text-gray-600 md:text-base">
                Manage all program graduates and alumni
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white shadow-sm transition-colors hover:bg-blue-700"
            >
              <HiPlus className="h-5 w-5" />
              Add New Alumni
            </motion.button>
          </div>
        </div>
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4"
            >
              <div className="flex items-center gap-2">
                <HiExclamationTriangle className="h-5 w-5 text-red-500" />
                <span className="text-red-700">{error}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
              <p className="mt-2 text-gray-600">Loading alumni...</p>
            </div>
          ) : alumni.length === 0 ? (
            <div className="p-8 text-center">
              <HiUser className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-black">No alumni found</h3>
              <p className="mt-1 text-gray-600">Get started by adding your first alumni.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiUser className="h-4 w-4" /> Name
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiEnvelope className="h-4 w-4" /> Email
                      </div>
                    </th>

                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiPhone className="h-4 w-4" /> Phone
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiBuildingOffice2 className="h-4 w-4" /> Business Stage
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {alumni.map((alumni, index) => (
                    <motion.tr
                      key={alumni.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-blue-700 text-white">
                            {(alumni.organization ||
                              alumni.email ||
                              alumni.id)?.[0]?.toUpperCase() || 'A'}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-black">
                              {alumni.organization || ''}
                            </div>
                            <div className="text-sm text-gray-600">
                              ID: {alumni.id?.substring(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-black">{alumni.email}</div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-black">{alumni.phone}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-black">{alumni.businessStage}</div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleView(alumni)}
                            className="group relative rounded-lg bg-gray-100 p-2 text-gray-700 transition-colors hover:bg-gray-200"
                            title="View Details"
                          >
                            <HiEye className="h-4 w-4" />
                            <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                              View Details
                            </span>
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleDeleteClick(alumni)}
                            className="group relative rounded-lg bg-red-100 p-2 text-red-700 transition-colors hover:bg-red-200"
                            title="Delete Alumni"
                          >
                            <HiTrash className="h-4 w-4" />
                            <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                              Delete Alumni
                            </span>
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleMakeClient(alumni)}
                            className="group relative rounded-lg bg-green-100 p-2 text-green-700 transition-colors hover:bg-green-200"
                            title="Make Client"
                          >
                            <HiCheckCircle className="h-4 w-4" />
                            <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                              Make Client
                            </span>
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Alumni</p>
                <p className="text-2xl font-bold text-black">{alumni.length}</p>
              </div>
              <div className="rounded-full bg-blue-100 p-3">
                <HiUser className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-black">
                  {alumni.filter(a => a.status === 'Active').length}
                </p>
              </div>
              <div className="rounded-full bg-green-100 p-3">
                <HiCheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Business Stages</p>
                <p className="text-2xl font-bold text-black">
                  {new Set(alumni.map(a => a.businessStage)).size}
                </p>
              </div>
              <div className="rounded-full bg-blue-100 p-3">
                <HiBuildingOffice2 className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Graduated This Year</p>
                <p className="text-2xl font-bold text-black">
                  {
                    alumni.filter(a => {
                      const createdAt = a.createdAt?.toDate?.() || new Date(a.createdAt);
                      const now = new Date();
                      return createdAt.getFullYear() === now.getFullYear();
                    }).length
                  }
                </p>
              </div>
              <div className="rounded-full bg-blue-100 p-3">
                <HiPencil className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>
        <AnimatePresence>
          {showModal && selectedAlumni && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            >
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.95 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl"
              >
                <div className="sticky top-0 flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-slate-600 to-slate-700 text-lg font-bold text-white shadow-sm">
                      {(selectedAlumni.firstname ||
                        selectedAlumni.email ||
                        selectedAlumni.id)?.[0]?.toUpperCase() || 'A'}
                    </div>
                    <div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-base font-semibold text-gray-900">
                          {selectedAlumni.firstname || ''} {selectedAlumni.lastname || ''}
                        </span>
                        <span className="text-xs text-gray-500">{selectedAlumni.email}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    onClick={() => setShowModal(false)}
                    aria-label="Close"
                  >
                    <HiXMark className="h-6 w-6" />
                  </button>
                </div>
                <div className="space-y-6 p-6">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <HiEnvelope className="h-4 w-4" /> Email
                      </label>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        {selectedAlumni.email}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <HiPhone className="h-4 w-4" /> Phone
                      </label>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        {selectedAlumni.phone}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <HiBuildingOffice2 className="h-4 w-4" /> Business Stage
                      </label>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        {selectedAlumni.businessStage}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showCreateModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            >
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.95 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-2xl"
              >
                <form onSubmit={handleCreateAlumni} className="space-y-6 p-6">
                  <h2 className="text-xl font-bold text-gray-900">Create New Alumni</h2>

                  {/* Personal Information */}
                  <div>
                    <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-blue-900/80">
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <input
                        type="text"
                        required
                        placeholder="First Name"
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={createForm.firstname}
                        onChange={e => setCreateForm({ ...createForm, firstname: e.target.value })}
                      />
                      <input
                        type="text"
                        required
                        placeholder="Last Name"
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={createForm.lastname}
                        onChange={e => setCreateForm({ ...createForm, lastname: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-blue-900/80">
                      Contact Information
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <input
                        type="email"
                        required
                        placeholder="Email"
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={createForm.email}
                        onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
                      />
                      <input
                        type="text"
                        required
                        placeholder="Phone Number"
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={createForm.phone}
                        onChange={e => setCreateForm({ ...createForm, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Business Information */}
                  <div>
                    <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-blue-900/80">
                      Business Information
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <input
                        type="text"
                        required
                        placeholder="Organization"
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={createForm.organization}
                        onChange={e =>
                          setCreateForm({ ...createForm, organization: e.target.value })
                        }
                      />
                      <select
                        required
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={createForm.businessStage}
                        onChange={e =>
                          setCreateForm({ ...createForm, businessStage: e.target.value })
                        }
                      >
                        <option value="">Select Business Stage</option>
                        <option value="Idea Stage">Idea Stage</option>
                        <option value="Startup">Startup</option>
                        <option value="Growth">Growth</option>
                        <option value="Established">Established</option>
                        <option value="Scaling">Scaling</option>
                        <option value="Revenue Generation +5 years">
                          Revenue Generation +5 years
                        </option>
                      </select>
                      <select
                        required
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        value={createForm.program}
                        onChange={e => setCreateForm({ ...createForm, program: e.target.value })}
                      >
                        <option value="">Select Program</option>
                        <option value="Business Acceleration Scaling Program">
                          Business Acceleration Scaling Program
                        </option>
                        <option value="Startup Incubation Program">
                          Startup Incubation Program
                        </option>
                        <option value="Business Development Program">
                          Business Development Program
                        </option>
                      </select>
                    </div>
                  </div>

                  {/* Auto-assigned Information */}
                  <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-4">
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-blue-900/80">
                      Auto-assigned Information
                    </h3>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <HiLockClosed className="h-4 w-4 text-blue-500" />
                        <span>Default Password:</span>
                        <span className="rounded bg-gray-100 px-2 py-1 font-mono text-xs">
                          Password@12345
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <HiShieldCheck className="h-4 w-4 text-blue-500" />
                        <span>Status:</span>
                        <span className="font-medium text-green-600">Active</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <HiUserGroup className="h-4 w-4 text-blue-500" />
                        <span>Role:</span>
                        <span className="font-medium text-blue-600">Alumni</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <HiShieldCheck className="h-4 w-4 text-blue-500" />
                        <span>UID Consistency:</span>
                        <span className="font-medium text-green-600">Firebase Auth UID</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={creating}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-60"
                    >
                      {creating ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showPasswordModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            >
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.95 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
              >
                <h2 className="mb-4 text-lg font-bold">Alumni Created</h2>
                <p className="mb-2">Share this password with the alumni for their first login:</p>
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-lg text-blue-700">
                  Password@12345
                </div>
                <button
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                  onClick={() => setShowPasswordModal(false)}
                >
                  Done
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {editAlumni && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            >
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.95 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-2xl"
              >
                <form onSubmit={handleUpdateAlumni} className="space-y-6 p-6">
                  <h2 className="text-xl font-bold text-gray-900">Edit Alumni</h2>
                  <div className="grid grid-cols-1 gap-4">
                    <input
                      type="text"
                      required
                      placeholder="First Name"
                      className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      value={editForm.firstname}
                      onChange={e => setEditForm({ ...editForm, firstname: e.target.value })}
                    />
                    <input
                      type="text"
                      required
                      placeholder="Last Name"
                      className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      value={editForm.lastname}
                      onChange={e => setEditForm({ ...editForm, lastname: e.target.value })}
                    />
                    <input
                      type="email"
                      required
                      placeholder="Email"
                      className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      value={editForm.email}
                      onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                    />
                    <input
                      type="text"
                      required
                      placeholder="Phone Number"
                      className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      value={editForm.phone}
                      onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setEditAlumni(null)}
                      disabled={updating}
                      className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-60"
                    >
                      {updating ? 'Updating...' : 'Update'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {toast && (
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          )}
        </AnimatePresence>

        <ConfirmationModal
          isOpen={deleteConfirm.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Alumni"
          message={`Are you sure you want to delete ${deleteConfirm.alumni?.firstname || deleteConfirm.alumni?.organization || ''}? They will be moved to deleted users.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
};

export default AlumniTable;
